import {
  IProduct,
  IRateReqSchemaUI,
  IRateResSchema,
  OfferingTypesEnum,
} from '@monetize/types/app';
import { useEffect, useRef } from 'react';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import { convertRateResToReq } from '../../../../../../utils/rates';

interface UseRateFormHandlersParams {
  id: string;
  rateId?: string;
  products: IProduct[];
  type: OfferingTypesEnum;
  defaultCurrency: string;
  isNewRate: boolean;
  isClonedRate: boolean;
  isDirty: boolean;
  offeringRate: IRateResSchema | null;
  setValue: UseFormSetValue<IRateReqSchemaUI>;
  reset: UseFormReset<IRateReqSchemaUI>;
  fetchOfferingRate: (id: string) => void;
  handleDirtyRateIds: (rateId: string, type: 'add' | 'remove') => void;
}

/**
 * Custom hook to manage rate form side effects and initialization
 */
export const useRateFormHandlers = ({
  id,
  rateId,
  products,
  type,
  defaultCurrency,
  isNewRate,
  isClonedRate,
  isDirty,
  offeringRate,
  setValue,
  reset,
  fetchOfferingRate,
  handleDirtyRateIds,
}: UseRateFormHandlersParams) => {
  // Initialize rate on mount. Deliberately not keyed on `isFetching`: every
  // fetch flips it, so the effect re-fetched each time the last one settled
  // and the form swapped between skeleton and accordion without end. A
  // fetch already in flight is joined by `fetchQuery`, not repeated.
  useEffect(() => {
    if (id) {
      if (!(isNewRate || isClonedRate)) {
        fetchOfferingRate(id);
      }
      setValue('id', id);

      if (isNewRate) {
        setValue('quotable', true);
      }
    }
  }, [fetchOfferingRate, id, isClonedRate, isNewRate, setValue]);

  const lastResetKeyRef = useRef('');

  // Reset form when offering rate or meaningful data changes.
  useEffect(() => {
    if (offeringRate) {
      const productKey = products
        .map(({ id: productId, productType }) => `${productId}:${productType}`)
        .join(',');
      const resetKey = `${offeringRate.id}-${offeringRate.modifyDate}-${productKey}-${defaultCurrency}-${type}`;
      if (lastResetKeyRef.current !== resetKey) {
        lastResetKeyRef.current = resetKey;
        reset(
          convertRateResToReq(offeringRate, products, defaultCurrency, type),
        );
      }
    }
  }, [offeringRate, products, defaultCurrency, type, reset]);

  // Track dirty state
  useEffect(() => {
    if (rateId) {
      handleDirtyRateIds(rateId, isDirty ? 'add' : 'remove');
    }
  }, [rateId, isDirty, handleDirtyRateIds]);

  // Initialize product types for new rates
  useEffect(() => {
    if (isNewRate && products.length) {
      products.forEach(({ id: productId, productType }) => {
        setValue(`products.${productId}.productType`, productType);
      });
    }
  }, [isNewRate, products, setValue]);
};

/**
 * Creates handler functions for rate form actions
 */
export const createRateFormHandlers = ({
  values,
  offeringRate,
  handleCloneRate,
  onRemove,
  handleDirtyRateIds,
  id,
  reset,
  removeOfferingRate,
}: {
  values: IRateReqSchemaUI;
  offeringRate: IRateResSchema | null;
  handleCloneRate: (data: IRateReqSchemaUI) => void;
  onRemove?: () => void;
  handleDirtyRateIds: (rateId: string, type: 'add' | 'remove') => void;
  id: string;
  reset: UseFormReset<IRateReqSchemaUI>;
  removeOfferingRate: (rateId: string) => Promise<boolean>;
}) => {
  const handleReset = () => {
    if (offeringRate?.id) {
      reset();
    }
  };

  const handleClone = () => {
    handleCloneRate(values);
  };

  const handleRemove = async () => {
    if (offeringRate?.id) {
      const res = await removeOfferingRate(offeringRate.id);
      if (res && onRemove) {
        onRemove();
        handleDirtyRateIds(offeringRate.id, 'remove');
      }
    }

    if (onRemove && !offeringRate?.id) {
      onRemove();
      handleDirtyRateIds(id, 'remove');
    }
  };

  return {
    handleReset,
    handleClone,
    handleRemove,
  };
};
